import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Table, Button, Spin, Alert, Descriptions, Typography, Space, Row, Col, Tag, Divider, Card, App, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { PrinterOutlined, ArrowLeftOutlined, CheckCircleOutlined, ClockCircleOutlined, FileTextOutlined, InfoCircleOutlined, DollarOutlined, CalendarOutlined } from '@ant-design/icons';
import { apiClient } from '../../utils/api';
import { PageTitle, StandardCard, PageWrapper, StatCard, StatCardsContainer } from '../common';
import { theme } from '../../theme';

const { Text, Title } = Typography;

interface Contrato {
  id: number;
  ncontrato: string;
  tipo: string;
  producto_nombre: string;
  nproducto_id: number;
  producto_tipo_id?: number;
  fecha: string;
  comprador_nombre: string;
  comprador_id: number;
  vendedor_nombre: string;
  vendedor_id: number;
  despachante_nombre: string;
  corredor_nombre: string;
  entregador_nombre: string;
  destinatario_nombre: string;
  empresa_nombre: string;
  estado: string;
  tarafardo: string;
  kilos: number;
  precio: string;
  fecha_contrato: string;
  fecha_cierre: string;
}

interface Fardo {
  id: number;
  nro_fardo: number;
  nro_lote: number;
  id_origen: number;
  contrato_vendedor_nombre: string;
  estado: string;
  alta: number;
  nro_orden?: number;
  fecha_orden?: string;
  tiene_recepcion_despacho?: boolean;
}

interface Orden {
  nro_orden: number;
  fecha_orden: string;
  tipo_orden: string;
  total_detalles: number;
  tiene_recepcion_despacho: boolean;
  tipo_recepcion_despacho: string | null;
  fecha_recepcion_despacho: string | null;
}

interface Despacho {
  numero_ticket: number;
  numero_remito: string;
  dtv: string;
  transportista: string;
  kilos: number;
  fecha: string;
  cierre: string;
}

interface EmisionData {
  contrato: Contrato;
  fardos: Fardo[];
  total_fardos: number;
  fardos_entregados: number;
  fardos_pendientes: number;
  total_kilos_entregados?: number;
  despachos?: Despacho[];
  empresa_id: number;
  contrato_id: number;
}

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
    gap: '16px',
    flexWrap: 'wrap' as const,
    paddingBottom: '16px',
    borderBottom: `2px solid ${theme.colors.borderLight}`,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    alignItems: 'center',
    padding: '80px 20px',
    minHeight: '500px',
    background: `linear-gradient(135deg, ${theme.colors.backgroundGray} 0%, ${theme.colors.background} 100%)`,
    borderRadius: theme.styles.borderRadius,
  },
  errorContainer: {
    marginTop: '24px',
  },
  lotesContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '16px',
    marginTop: '16px',
  },
  loteBadge: {
    backgroundColor: theme.colors.background,
    border: `2px solid ${theme.colors.borderLight}`,
    borderRadius: theme.styles.borderRadius,
    padding: '16px',
    transition: 'all 0.3s ease',
    boxShadow: theme.styles.boxShadowLight,
    cursor: 'pointer',
  },
  infoCard: {
    background: `linear-gradient(135deg, ${theme.colors.primaryDark}15 0%, ${theme.colors.info}10 100%)`,
    border: `1px solid ${theme.colors.borderLight}`,
    borderRadius: theme.styles.borderRadius,
    padding: '20px',
    marginBottom: '24px',
  },
  statCardEnhanced: {
    borderRadius: theme.styles.borderRadius,
    boxShadow: theme.styles.boxShadowLight,
    border: 'none',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.styles.boxShadow,
    }
  },
};

// Utilidades de caché
const CACHE_KEY_PREFIX = 'emision_contrato_';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

interface CacheData {
  data: EmisionData;
  timestamp: number;
  id_contrato: number;
  id_empresa: number;
}

const getCacheKey = (id_empresa: number, id_contrato: number): string => {
  return `${CACHE_KEY_PREFIX}${id_empresa}_${id_contrato}`;
};

const getCachedData = (id_empresa: number, id_contrato: number): EmisionData | null => {
  try {
    const cacheKey = getCacheKey(id_empresa, id_contrato);
    const cached = sessionStorage.getItem(cacheKey);
    if (!cached) return null;

    const cacheData: CacheData = JSON.parse(cached);
    const now = Date.now();

    // Verificar que el caché no haya expirado y que sea del mismo contrato
    if (now - cacheData.timestamp < CACHE_DURATION && cacheData.id_contrato === id_contrato) {
      console.log(`[emisioncontratos] ✅ Datos encontrados en caché (edad: ${Math.round((now - cacheData.timestamp) / 1000)}s)`);
      return cacheData.data;
    } else {
      // Limpiar caché expirado
      sessionStorage.removeItem(cacheKey);
      console.log(`[emisioncontratos] ⏰ Caché expirado, limpiando...`);
    }
  } catch (error) {
    console.warn('[emisioncontratos] Error al leer caché:', error);
  }
  return null;
};

const setCachedData = (id_empresa: number, id_contrato: number, data: EmisionData): void => {
  const cacheKey = getCacheKey(id_empresa, id_contrato);
  const dataToCache: CacheData = {
    data,
    timestamp: Date.now(),
    id_contrato,
    id_empresa,
  };

  try {
    sessionStorage.setItem(cacheKey, JSON.stringify(dataToCache));
    console.log(`[emisioncontratos] 💾 Datos guardados en caché`);
  } catch (error) {
    console.warn('[emisioncontratos] Error al guardar en caché:', error);
    // Si sessionStorage está lleno, limpiar cachés antiguos
    try {
      const keys = Object.keys(sessionStorage);
      const oldCacheKeys = keys.filter(k => k.startsWith(CACHE_KEY_PREFIX));
      if (oldCacheKeys.length > 0) {
        // Eliminar el más antiguo
        let oldestKey = oldCacheKeys[0];
        let oldestTime = Infinity;
        oldCacheKeys.forEach(key => {
          try {
            const cached = sessionStorage.getItem(key);
            if (cached) {
              const parsedCacheData: CacheData = JSON.parse(cached);
              if (parsedCacheData.timestamp < oldestTime) {
                oldestTime = parsedCacheData.timestamp;
                oldestKey = key;
              }
            }
          } catch { }
        });
        sessionStorage.removeItem(oldestKey);
        // Intentar guardar de nuevo
        sessionStorage.setItem(cacheKey, JSON.stringify(dataToCache));
      }
    } catch { }
  }
};

const EmisionContratos: React.FC = () => {
  const { getAccessTokenSilently, user, isAuthenticated } = useAuth0();
  const { message: messageApi } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const loadingRef = useRef(false); // Ref para evitar múltiples llamadas simultáneas
  const abortControllerRef = useRef<AbortController | null>(null); // Ref para el AbortController actual
  const currentRequestIdRef = useRef<string | null>(null); // ID único de la petición actual
  const [data, setData] = useState<EmisionData | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false); // Para indicar si está refrescando datos en caché

  // Obtener datos del estado de navegación
  const { id_contrato, ncontrato } = location.state || {};

  useEffect(() => {
    // Validación temprana antes de cualquier operación async
    if (!isAuthenticated || !id_contrato || !user?.id_empresa) {
      setCargando(false);
      if (!isAuthenticated) {
        setError('Por favor, inicie sesión para ver los datos');
      } else if (!id_contrato) {
        setError('No se proporcionó el ID del contrato');
      } else {
        setError('No se encontró la empresa asociada a su usuario');
      }
      return;
    }

    const id_empresa = user.id_empresa;

    // Intentar cargar datos del caché primero
    const cachedData = getCachedData(id_empresa, id_contrato);
    if (cachedData) {
      setData(cachedData);
      setCargando(false);
      setIsRefreshing(true); // Indicar que estamos refrescando en segundo plano
      console.log(`[emisioncontratos] 📦 Mostrando datos en caché, actualizando en segundo plano...`);
    } else {
      // Si no hay caché, mantener loading en true
      setCargando(true);
      setIsRefreshing(false);
    }

    // Generar ID único para esta ejecución
    const requestId = `${id_contrato}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Cancelar petición anterior si existe
    if (abortControllerRef.current && currentRequestIdRef.current !== requestId) {
      console.log(`[emisioncontratos] Cancelando petición anterior [${currentRequestIdRef.current}]...`);
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Establecer esta ejecución como la actual
    currentRequestIdRef.current = requestId;
    loadingRef.current = true;

    let isMounted = true;

    console.log(`[emisioncontratos] [${requestId}] Iniciando efecto...`);

    const cargarDatos = async (requestId: string) => {
      // Verificar que esta sigue siendo la petición actual
      if (currentRequestIdRef.current !== requestId) {
        console.log(`[emisioncontratos] [${requestId}] Esta petición fue reemplazada por una más reciente, cancelando...`);
        return;
      }

      const startTime = Date.now();

      try {
        setCargando(true);
        setError(null);

        console.log(`[emisioncontratos] [${requestId}] Iniciando carga de datos...`);
        const token = await getAccessTokenSilently();
        const id_empresa = user?.id_empresa;

        if (!process.env.REACT_APP_ORDENES_URL) {
          throw new Error('Error de configuración: URL de órdenes no definida');
        }

        // Verificar nuevamente antes de hacer la petición
        if (currentRequestIdRef.current !== requestId) {
          console.log(`[emisioncontratos] [${requestId}] Petición cancelada antes de enviar`);
          return;
        }

        const url = `${process.env.REACT_APP_ORDENES_URL}/emision_contratos/${id_empresa}/${id_contrato}/`;
        console.log(`[emisioncontratos] [${requestId}] URL:`, url);
        console.log(`[emisioncontratos] [${requestId}] ID Empresa:`, id_empresa, 'ID Contrato:', id_contrato);
        console.log(`[emisioncontratos] [${requestId}] Enviando petición con timeout de 180 segundos...`);

        // Usar fetch nativo con AbortController para mejor control del timeout
        const controller = new AbortController();
        abortControllerRef.current = controller; // Guardar referencia para poder cancelar

        const timeoutId = setTimeout(() => {
          if (currentRequestIdRef.current === requestId) {
            console.log(`[emisioncontratos] [${requestId}] ⏱️ Timeout alcanzado, abortando petición...`);
            controller.abort();
          }
        }, 180000); // 180 segundos (3 minutos)

        try {
          const response = await fetch(url, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            signal: controller.signal,
          });

          clearTimeout(timeoutId);

          // Verificar que esta sigue siendo la petición actual antes de procesar
          if (currentRequestIdRef.current !== requestId) {
            console.log(`[emisioncontratos] [${requestId}] Respuesta recibida pero esta petición fue reemplazada, ignorando...`);
            return;
          }

          if (!response.ok) {
            const errorText = await response.text();
            console.error(`[emisioncontratos] [${requestId}] ❌ Error HTTP:`, response.status, errorText);
            throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
          }

          const responseData = await response.json();

          // Verificar nuevamente antes de establecer datos
          if (currentRequestIdRef.current !== requestId) {
            console.log(`[emisioncontratos] [${requestId}] Datos recibidos pero esta petición fue reemplazada, ignorando...`);
            return;
          }

          const elapsedTime = Date.now() - startTime;
          console.log(`[emisioncontratos] [${requestId}] ✅ Respuesta recibida después de ${elapsedTime}ms`);
          console.log(`[emisioncontratos] [${requestId}] Status:`, response.status);
          console.log(`[emisioncontratos] [${requestId}] Datos recibidos:`, responseData);

          if (isMounted && loadingRef.current && currentRequestIdRef.current === requestId) {
            if (responseData) {
              // Guardar en caché antes de establecer los datos
              setCachedData(id_empresa, id_contrato, responseData);
              setData(responseData);
              setCargando(false);
              setIsRefreshing(false);
              console.log(`[emisioncontratos] [${requestId}] ✅ Datos establecidos correctamente`);
            } else {
              throw new Error('No se recibieron datos en la respuesta');
            }
          } else {
            console.log(`[emisioncontratos] [${requestId}] ⚠️ Componente desmontado o carga cancelada, ignorando respuesta`);
          }
        } catch (fetchError: any) {
          clearTimeout(timeoutId);

          // Verificar si esta petición fue reemplazada
          if (currentRequestIdRef.current !== requestId) {
            console.log(`[emisioncontratos] [${requestId}] Error en petición reemplazada, ignorando...`);
            return;
          }

          // Si es un error de abort (timeout o cancelación), verificar si fue cancelado intencionalmente
          if (fetchError.name === 'AbortError') {
            // Si el componente ya no está montado o fue cancelado, no lanzar error
            if (!isMounted || !loadingRef.current || currentRequestIdRef.current !== requestId) {
              console.log(`[emisioncontratos] [${requestId}] Petición cancelada (componente desmontado o nueva petición iniciada)`);
              return; // Salir silenciosamente
            }
            throw new Error('TIMEOUT: La petición tardó más de 3 minutos');
          }

          throw fetchError;
        }
      } catch (err: any) {
        // Verificar si esta petición fue reemplazada antes de manejar el error
        if (currentRequestIdRef.current !== requestId) {
          console.log(`[emisioncontratos] [${requestId}] Error en petición reemplazada, ignorando...`);
          return;
        }

        const elapsedTime = Date.now() - startTime;
        console.error(`[emisioncontratos] [${requestId}] ❌ Error después de ${elapsedTime}ms:`, err);
        console.error(`[emisioncontratos] [${requestId}] Detalles del error:`, {
          message: err.message,
          name: err.name,
          stack: err.stack
        });

        if (isMounted && loadingRef.current && currentRequestIdRef.current === requestId) {
          let errorMessage = 'Error desconocido';

          if (err.message?.includes('TIMEOUT') || err.name === 'AbortError') {
            errorMessage = 'La petición tardó más de 3 minutos. El servidor puede estar procesando muchos datos. Por favor, intente nuevamente más tarde o contacte al administrador si el problema persiste.';
          } else if (err.message?.includes('404')) {
            errorMessage = 'No se encontraron datos para este contrato.';
          } else if (err.message?.includes('403')) {
            errorMessage = 'No tiene permisos para ver este contrato.';
          } else if (err.message?.includes('500')) {
            errorMessage = 'Error del servidor. Por favor, contacte al administrador.';
          } else if (err.message) {
            errorMessage = err.message;
          }

          setError(errorMessage);
          setCargando(false);
          setIsRefreshing(false);
        }
      } finally {
        loadingRef.current = false;
      }
    };

    // Cargar datos siempre (en segundo plano si hay caché)
    cargarDatos(requestId);

    return () => {
      console.log(`[emisioncontratos] [${requestId}] 🧹 Limpiando efecto...`);
      isMounted = false;

      // Solo cancelar si esta sigue siendo la petición actual
      if (currentRequestIdRef.current === requestId) {
        loadingRef.current = false;
        currentRequestIdRef.current = null;

        // Cancelar petición si existe
        if (abortControllerRef.current) {
          console.log(`[emisioncontratos] [${requestId}] Cancelando petición en cleanup...`);
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }
      } else {
        console.log(`[emisioncontratos] [${requestId}] Cleanup ignorado, hay una petición más reciente`);
      }
    };
  }, [user?.id_empresa, isAuthenticated, id_contrato, getAccessTokenSilently]);

  const handleVolver = useCallback(() => {
    navigate('/listadocontratos');
  }, [navigate]);

  const handleGenerarPDF = useCallback(async () => {
    if (!data) return;

    // Mostrar indicador de carga mientras se genera el PDF
    const loadingMessage = messageApi?.loading?.('Generando PDF...', 0);

    try {
      // Lazy loading de jsPDF solo cuando se necesita
      const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
        import('jspdf'),
        import('jspdf-autotable')
      ]);

      // Crear nuevo documento PDF
      const doc = new jsPDF();

      // Configurar fuente y tamaños
      doc.setFont('helvetica');
      doc.setFontSize(20);

      // Título principal
      doc.setTextColor(24, 144, 255); // Color celeste
      doc.text('EMISIÓN CONTRATO', 105, 20, { align: 'center' });

      // Información del contrato
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);

      let yPosition = 40;

      // Datos del contrato
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('INFORMACIÓN DEL CONTRATO', 20, yPosition);
      yPosition += 10;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');

      const contratoData = [
        ['Número de Contrato:', data.contrato.ncontrato],
        ['Tipo:', data.contrato.tipo === 'C' ? 'Compra' : 'Venta'],
        ['Producto:', data.contrato.producto_nombre],
        ['Fecha:', new Date(data.contrato.fecha).toLocaleDateString('es-ES')],
        ['Estado:', data.contrato.estado],
        ['Comprador:', data.contrato.comprador_nombre],
        ['Vendedor:', data.contrato.vendedor_nombre],
        ['Despachante:', data.contrato.despachante_nombre || 'No especificado'],
        ['Corredor:', data.contrato.corredor_nombre || 'No especificado'],
        ['Entregador:', data.contrato.entregador_nombre || 'No especificado'],
        ['Destinatario:', data.contrato.destinatario_nombre || 'No especificado'],
        ['Tara Fardo:', `${data.contrato.tarafardo} kg`],
        ['Kilos:', `${data.contrato.kilos.toLocaleString()} kg`],
        ['Precio:', `$${data.contrato.precio}`],
        ['Fecha Contrato:', new Date(data.contrato.fecha_contrato).toLocaleDateString('es-ES')],
        ['Fecha Cierre:', new Date(data.contrato.fecha_cierre).toLocaleDateString('es-ES')],
      ];

      contratoData.forEach(([label, value]) => {
        doc.text(label, 20, yPosition);
        doc.text(value, 80, yPosition);
        yPosition += 6;
      });

      // Resumen de fardos
      yPosition += 10;
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('RESUMEN DE FARDOS', 20, yPosition);
      yPosition += 10;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Total de Fardos: ${data.total_fardos}`, 20, yPosition);
      yPosition += 6;
      doc.text(`Entregados/Recepcionados: ${data.fardos_entregados || 0}`, 20, yPosition);
      yPosition += 6;
      doc.text(`Pendientes: ${data.fardos_pendientes || 0}`, 20, yPosition);
      yPosition += 6;
      doc.text(`Empresa: ${data.contrato.empresa_nombre}`, 20, yPosition);
      yPosition += 10;

      // Resumen por lotes (calcular dentro de la función)
      if (data.fardos && data.fardos.length > 0) {
        const lotesMap = new Map<number, { lote: number; cantidad: number; origen: string }>();

        data.fardos.forEach(fardo => {
          if (lotesMap.has(fardo.nro_lote)) {
            const lote = lotesMap.get(fardo.nro_lote)!;
            lote.cantidad += 1;
          } else {
            lotesMap.set(fardo.nro_lote, {
              lote: fardo.nro_lote,
              cantidad: 1,
              origen: fardo.contrato_vendedor_nombre
            });
          }
        });

        const lotesResumenPDF = Array.from(lotesMap.values()).sort((a, b) => a.lote - b.lote);

        if (lotesResumenPDF.length > 0) {
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text('RESUMEN POR LOTES:', 20, yPosition);
          yPosition += 8;

          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');

          lotesResumenPDF.forEach((lote) => {
            doc.text(`Lote ${lote.lote}: ${lote.cantidad} fardos - ${lote.origen}`, 25, yPosition);
            yPosition += 5;
          });
          yPosition += 5;
        }
      }

      // Tabla de fardos
      if (data.fardos.length > 0) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('FARDOS APLICADOS', 20, yPosition);
        yPosition += 10;

        // Preparar datos para la tabla
        const tableData = data.fardos.map(fardo => [
          fardo.nro_fardo.toString(),
          fardo.nro_lote.toString(),
          fardo.contrato_vendedor_nombre,
          fardo.nro_orden ? fardo.nro_orden.toString() : '-',
          fardo.fecha_orden ? new Date(fardo.fecha_orden).toLocaleDateString('es-ES') : '-',
          fardo.estado
        ]);
        console.log('tableData', tableData);
        // Agregar tabla usando autoTable
        autoTable(doc, {
          head: [['Nro Fardo', 'Nro Lote', 'Origen', 'Nº Orden', 'Fecha', 'Estado']],
          body: tableData,
          startY: yPosition,
          headStyles: {
            fillColor: [24, 144, 255],
            textColor: 255,
            fontStyle: 'bold'
          },
          styles: {
            fontSize: 8,
            cellPadding: 2
          },
          columnStyles: {
            0: { cellWidth: 25 },
            1: { cellWidth: 25 },
            2: { cellWidth: 60 },
            3: { cellWidth: 25 },
            4: { cellWidth: 30 },
            5: { cellWidth: 25 }
          }
        });
      }

      // Guardar el PDF
      const fileName = `Emision_Contrato_${data.contrato.ncontrato}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);

      // Cerrar mensaje de carga
      if (loadingMessage) {
        loadingMessage();
      }
      messageApi?.success('PDF generado correctamente');
    } catch (error) {
      // Cerrar mensaje de carga en caso de error
      if (loadingMessage) {
        loadingMessage();
      }
      console.error('Error al generar PDF:', error);
      messageApi?.error('Error al generar el PDF. Por favor, intente nuevamente.');
    }
  }, [data, messageApi]);

  // Calcular resumen por lotes (memoizado)
  const lotesResumen = useMemo(() => {
    if (!data?.fardos) return [];

    const lotesMap = new Map<number, { lote: number; cantidad: number; origen: string }>();

    data.fardos.forEach(fardo => {
      if (lotesMap.has(fardo.nro_lote)) {
        const lote = lotesMap.get(fardo.nro_lote)!;
        lote.cantidad += 1;
      } else {
        lotesMap.set(fardo.nro_lote, {
          lote: fardo.nro_lote,
          cantidad: 1,
          origen: fardo.contrato_vendedor_nombre
        });
      }
    });

    return Array.from(lotesMap.values()).sort((a, b) => a.lote - b.lote);
  }, [data?.fardos]);

  // Usar los totales calculados en el backend (memoizado)
  const totalesOrdenes = useMemo(() => ({
    entregados: data?.fardos_entregados || 0,
    pendientes: data?.fardos_pendientes || 0
  }), [data?.fardos_entregados, data?.fardos_pendientes]);

  // Memoizar funciones render para evitar recrearlas en cada render
  const renderNroOrden = useCallback((nro_orden: number, record: Fardo) => {
    if (record.tiene_recepcion_despacho && nro_orden) {
      return nro_orden;
    }
    return '-';
  }, []);

  const renderFechaOrden = useCallback((fecha_orden: string, record: Fardo) => {
    if (record.tiene_recepcion_despacho && fecha_orden) {
      return new Date(fecha_orden).toLocaleDateString('es-ES');
    }
    return '-';
  }, []);

  const renderEstado = useCallback((estado: string) => (
    <Tag
      color={estado === 'Activo' ? 'success' : 'error'}
      style={{
        fontWeight: 600,
        fontSize: '12px',
        padding: '2px 8px'
      }}
    >
      {estado}
    </Tag>
  ), []);

  // Memoizar columnas para evitar recrearlas en cada render
  const columns: ColumnsType<Fardo> = useMemo(() => [
    {
      title: 'Nro Fardo',
      dataIndex: 'nro_fardo',
      key: 'nro_fardo',
      width: 120,
      align: 'center' as const,
    },
    {
      title: 'Nro Lote',
      dataIndex: 'nro_lote',
      key: 'nro_lote',
      width: 120,
      align: 'center' as const,
    },
    {
      title: 'Origen',
      dataIndex: 'origen_nombre',
      key: 'origen_nombre',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Nº Orden',
      dataIndex: 'nro_orden',
      key: 'nro_orden',
      width: 100,
      align: 'center' as const,
      render: renderNroOrden,
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha_orden',
      key: 'fecha_orden',
      width: 120,
      align: 'center' as const,
      render: renderFechaOrden,
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      width: 100,
      align: 'center' as const,
      render: renderEstado,
    },
  ], [renderNroOrden, renderFechaOrden, renderEstado]);

  // Columnas para la tabla de despachos (tipo 3)
  const columnsDespachos: ColumnsType<Despacho> = useMemo(() => [
    {
      title: 'Nro Ticket',
      dataIndex: 'numero_ticket',
      key: 'numero_ticket',
      width: 120,
      align: 'center' as const,
    },
    {
      title: 'Nro Remito',
      dataIndex: 'numero_remito',
      key: 'numero_remito',
      width: 150,
      align: 'center' as const,
    },
    {
      title: 'DTV',
      dataIndex: 'dtv',
      key: 'dtv',
      width: 120,
      align: 'center' as const,
    },
    {
      title: 'Transportista',
      dataIndex: 'transportista',
      key: 'transportista',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Kilos',
      dataIndex: 'kilos',
      key: 'kilos',
      width: 120,
      align: 'center' as const,
      render: (value: number) => value.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      width: 120,
      align: 'center' as const,
      render: (fecha: string) => fecha ? new Date(fecha).toLocaleDateString('es-ES') : '-',
    },
  ], []);

  if (cargando) {
    return (
      <PageWrapper>
        <div style={styles.loadingContainer}>
          <Spin size="large" tip="Cargando datos del contrato..." />
          <Text style={{ marginTop: '16px', fontSize: '14px', color: theme.colors.textSecondary }}>
            Obteniendo información del contrato y fardos asociados...
          </Text>
        </div>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <div style={styles.errorContainer}>
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            action={
              <Button size="middle" onClick={handleVolver} icon={<ArrowLeftOutlined />}>
                Volver
              </Button>
            }
          />
        </div>
      </PageWrapper>
    );
  }

  if (!data) {
    return (
      <PageWrapper>
        <Alert
          message="Sin datos"
          description="No se encontraron datos para mostrar"
          type="warning"
          showIcon
          action={
            <Button size="middle" onClick={handleVolver} icon={<ArrowLeftOutlined />}>
              Volver
            </Button>
          }
        />
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <div style={styles.header}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <PageTitle level={2} style={{ marginBottom: '8px', fontSize: '28px', fontWeight: 700 }}>
              Emisión de Contrato
            </PageTitle>
            {isRefreshing && (
              <Tag color="processing" style={{ marginTop: '8px' }}>
                <ClockCircleOutlined /> Actualizando...
              </Tag>
            )}
          </div>
          <Text type="secondary" style={{ fontSize: '14px' }}>
            {data.contrato.ncontrato} • {data.contrato.producto_nombre}
          </Text>
        </div>
        <Space>
          <Button
            icon={<PrinterOutlined />}
            type="primary"
            onClick={handleGenerarPDF}
            size="large"
            style={{
              height: '40px',
              fontSize: '14px',
              borderRadius: theme.styles.borderRadius,
              backgroundColor: theme.colors.secondary,
              borderColor: theme.colors.secondary,
              fontWeight: 600,
              boxShadow: theme.styles.boxShadowLight,
            }}
          >
            Generar PDF
          </Button>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleVolver}
            size="large"
            style={{
              height: '40px',
              fontSize: '14px',
              borderRadius: theme.styles.borderRadius,
              borderColor: theme.colors.border,
            }}
          >
            Volver
          </Button>
        </Space>
      </div>

      {/* Resumen de Fardos - Destacado */}
      <StandardCard
        title={
          <Space>
            <FileTextOutlined style={{ color: theme.colors.primary }} />
            <span style={{ fontSize: '18px', fontWeight: 600 }}>
              {data.contrato.producto_tipo_id === 3 ? 'Resumen' : 'Resumen de Fardos'}
            </span>
          </Space>
        }
        style={{
          marginBottom: '24px',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadowLight,
        }}
      >
        <StatCardsContainer gap={20} minColumnWidth="220px">
          {data.contrato.producto_tipo_id === 3 ? (
            <StatCard
              title="Total Kilos"
              value={data.contrato.kilos}
              color={theme.colors.primary}
              formatNumber
              icon={<FileTextOutlined style={{ fontSize: '28px', color: theme.colors.primary }} />}
              style={styles.statCardEnhanced}
            />
          ) : (
            <StatCard
              title="Total de Fardos"
              value={data.total_fardos}
              color={theme.colors.primary}
              formatNumber
              icon={<FileTextOutlined style={{ fontSize: '28px', color: theme.colors.primary }} />}
              style={styles.statCardEnhanced}
            />
          )}
          {data.contrato.producto_tipo_id === 3 ? (
            <StatCard
              title="Total Kilos Entregados"
              value={data.total_kilos_entregados || 0}
              color={theme.colors.success}
              formatNumber
              icon={<CheckCircleOutlined style={{ fontSize: '28px', color: theme.colors.success }} />}
              style={styles.statCardEnhanced}
            />
          ) : (
            <StatCard
              title="Entregados/Recepcionados"
              value={totalesOrdenes.entregados}
              color={theme.colors.success}
              formatNumber
              icon={<CheckCircleOutlined style={{ fontSize: '28px', color: theme.colors.success }} />}
              style={styles.statCardEnhanced}
            />
          )}
          {data.contrato.producto_tipo_id === 3 ? (
            <StatCard
              title="Kilos Pendientes"
              value={(data.contrato.kilos || 0) - (data.total_kilos_entregados || 0)}
              color={theme.colors.warning}
              formatNumber
              icon={<ClockCircleOutlined style={{ fontSize: '28px', color: theme.colors.warning }} />}
              style={styles.statCardEnhanced}
            />
          ) : (
            <StatCard
              title="Pendientes"
              value={totalesOrdenes.pendientes}
              color={theme.colors.warning}
              formatNumber
              icon={<ClockCircleOutlined style={{ fontSize: '28px', color: theme.colors.warning }} />}
              style={styles.statCardEnhanced}
            />
          )}
        </StatCardsContainer>
      </StandardCard>

      {/* Información del Contrato */}
      <StandardCard
        title={
          <Space>
            <InfoCircleOutlined style={{ color: theme.colors.primary }} />
            <span style={{ fontSize: '18px', fontWeight: 600 }}>Información del Contrato</span>
          </Space>
        }
        style={{
          marginBottom: '24px',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadowLight,
        }}
      >
        <Descriptions
          bordered
          column={{ xs: 1, sm: 2, md: 3, lg: 3 }}
          size="middle"
          labelStyle={{
            fontWeight: 600,
            fontSize: '13px',
            padding: '12px 16px',
            backgroundColor: theme.colors.backgroundGray,
            width: '160px'
          }}
          contentStyle={{
            fontSize: '14px',
            padding: '12px 16px'
          }}
        >
          <Descriptions.Item label="Número de Contrato" span={1}>
            <Text strong style={{ fontSize: '15px', color: theme.colors.primary }}>{data.contrato.ncontrato}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Tipo" span={1}>
            <Tag
              color={data.contrato.tipo === 'C' ? 'blue' : 'green'}
              style={{
                fontWeight: 600,
                fontSize: '12px',
                padding: '4px 12px'
              }}
            >
              {data.contrato.tipo === 'C' ? 'Compra' : 'Venta'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Estado" span={1}>
            <Tag
              color={data.contrato.estado === 'Activa' ? 'success' : 'error'}
              style={{
                fontWeight: 600,
                fontSize: '12px',
                padding: '4px 12px'
              }}
            >
              {data.contrato.estado}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Producto" span={3}>
            <Text style={{ fontSize: '14px', fontWeight: 500 }}>{data.contrato.producto_nombre}</Text>
          </Descriptions.Item>
          <Descriptions.Item label={<><CalendarOutlined /> Fecha</>} span={1}>
            <Text>{new Date(data.contrato.fecha).toLocaleDateString('es-ES', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Fecha Contrato" span={1}>
            <Text>{new Date(data.contrato.fecha_contrato).toLocaleDateString('es-ES')}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Fecha Cierre" span={1}>
            <Text>{new Date(data.contrato.fecha_cierre).toLocaleDateString('es-ES')}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Comprador" span={1}>
            <Text>{data.contrato.comprador_nombre}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Vendedor" span={1}>
            <Text>{data.contrato.vendedor_nombre}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Empresa" span={1}>
            <Text strong style={{ color: theme.colors.primary }}>{data.contrato.empresa_nombre}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Despachante" span={1}>
            <Text>{data.contrato.despachante_nombre || <Text type="secondary">No especificado</Text>}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Corredor" span={1}>
            <Text>{data.contrato.corredor_nombre || <Text type="secondary">No especificado</Text>}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Entregador" span={1}>
            <Text>{data.contrato.entregador_nombre || <Text type="secondary">No especificado</Text>}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Destinatario" span={1}>
            <Text>{data.contrato.destinatario_nombre || <Text type="secondary">No especificado</Text>}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Tara Fardo" span={1}>
            <Text strong>{data.contrato.tarafardo} kg</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Kilos" span={1}>
            <Text strong style={{ fontSize: '15px', color: theme.colors.primary }}>
              {data.contrato.kilos.toLocaleString('es-AR')} kg
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label={<><DollarOutlined /> Precio</>} span={1}>
            <Text strong style={{ fontSize: '15px', color: theme.colors.success }}>
              ${data.contrato.precio}
            </Text>
          </Descriptions.Item>
        </Descriptions>

        {/* Resumen por Lotes */}
        {lotesResumen.length > 0 && (
          <>
            <Divider style={{ margin: '24px 0' }} />
            <div>
              <Title level={5} style={{
                marginBottom: '16px',
                color: theme.colors.primaryDark,
                fontSize: '16px',
                fontWeight: 600
              }}>
                Resumen por Lotes
              </Title>
              <div style={styles.lotesContainer}>
                {lotesResumen.map((lote) => (
                  <Card
                    key={lote.lote}
                    hoverable
                    style={{
                      borderRadius: theme.styles.borderRadius,
                      border: `2px solid ${theme.colors.borderLight}`,
                      transition: 'all 0.3s ease',
                    }}
                    bodyStyle={{ padding: '16px' }}
                  >
                    <div style={{
                      fontWeight: 700,
                      color: theme.colors.primary,
                      fontSize: '16px',
                      marginBottom: '8px'
                    }}>
                      Lote {lote.lote}
                    </div>
                    <div style={{
                      fontSize: '18px',
                      color: theme.colors.textPrimary,
                      fontWeight: 600,
                      marginBottom: '6px'
                    }}>
                      {lote.cantidad} {lote.cantidad === 1 ? 'fardo' : 'fardos'}
                    </div>
                    <Text type="secondary" style={{
                      fontSize: '12px',
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {lote.origen}
                    </Text>
                  </Card>
                ))}
              </div>
            </div>
          </>
        )}
      </StandardCard>

      {/* Tabla de Fardos o Detalle de Despachos */}
      {data.contrato.producto_tipo_id === 3 ? (
        <StandardCard
          title={
            <Space>
              <FileTextOutlined style={{ color: theme.colors.primary }} />
              <span style={{ fontSize: '18px', fontWeight: 600 }}>Detalle</span>
              {data.despachos && (
                <Tag color="blue" style={{ marginLeft: '8px' }}>
                  {data.despachos.length} {data.despachos.length === 1 ? 'despacho' : 'despachos'}
                </Tag>
              )}
            </Space>
          }
          style={{
            borderRadius: theme.styles.borderRadius,
            boxShadow: theme.styles.boxShadowLight,
          }}
        >
          {data.despachos && data.despachos.length > 0 ? (
            <Table<Despacho>
              dataSource={data.despachos}
              columns={columnsDespachos}
              rowKey={(record, index) => `despacho-${record.numero_ticket}-${index}`}
              pagination={{
                pageSize: 50,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ['10', '25', '50', '100'],
                showTotal: (total, range) => (
                  <Text strong style={{ color: theme.colors.textSecondary }}>
                    {range[0]}-{range[1]} de {total} despachos
                  </Text>
                ),
                position: ['bottomRight'],
                style: { marginTop: '16px' }
              }}
              size="middle"
              bordered
              scroll={{ x: 800, y: 'max(150px, calc(100vh - 500px))' }}
              style={{
                borderRadius: theme.styles.borderRadius
              }}
              rowClassName={(record, index) => {
                const baseClass = index % 2 === 0 ? 'table-row-even' : 'table-row-odd';
                const isNotClosed = record.cierre === '0';
                return isNotClosed ? `${baseClass} table-row-not-closed` : baseClass;
              }}
              onRow={(record, index) => {
                const isEven = index !== undefined && index % 2 === 0;
                const isNotClosed = record.cierre === '0';
                const baseBgColor = isNotClosed 
                  ? 'rgba(255, 77, 79, 0.1)' // Color rojizo claro
                  : (isEven ? theme.colors.background : theme.colors.backgroundGray);
                const hoverBgColor = isNotClosed
                  ? 'rgba(255, 77, 79, 0.2)' // Color rojizo más intenso al hover
                  : theme.colors.infoLight;
                
                return {
                  style: {
                    backgroundColor: baseBgColor,
                    transition: 'background-color 0.2s ease',
                  },
                  title: isNotClosed ? 'No está cerrado - Falta uno de los pesajes' : undefined,
                  onMouseEnter: (e) => {
                    e.currentTarget.style.backgroundColor = hoverBgColor;
                  },
                  onMouseLeave: (e) => {
                    e.currentTarget.style.backgroundColor = baseBgColor;
                  },
                };
              }}
            />
          ) : (
            <Alert
              message="Sin despachos"
              description="No se encontraron despachos para este contrato"
              type="info"
              showIcon
            />
          )}
        </StandardCard>
      ) : (
        <StandardCard
          title={
            <Space>
              <FileTextOutlined style={{ color: theme.colors.primary }} />
              <span style={{ fontSize: '18px', fontWeight: 600 }}>Fardos Aplicados</span>
              <Tag color="blue" style={{ marginLeft: '8px' }}>
                {data.fardos.length} {data.fardos.length === 1 ? 'fardo' : 'fardos'}
              </Tag>
            </Space>
          }
          style={{
            borderRadius: theme.styles.borderRadius,
            boxShadow: theme.styles.boxShadowLight,
          }}
        >
          <Table<Fardo>
            dataSource={data.fardos}
            columns={columns}
            rowKey="id"
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ['10', '25', '50', '100'],
              showTotal: (total, range) => (
                <Text strong style={{ color: theme.colors.textSecondary }}>
                  {range[0]}-{range[1]} de {total} fardos
                </Text>
              ),
              position: ['bottomRight'],
              style: { marginTop: '16px' }
            }}
            size="middle"
            bordered
            scroll={{ x: 800, y: 'max(150px, calc(100vh - 500px))' }}
            style={{
              borderRadius: theme.styles.borderRadius
            }}
            rowClassName={(record, index) =>
              index % 2 === 0 ? 'table-row-even' : 'table-row-odd'
            }
            onRow={(record, index) => {
              const isEven = index !== undefined && index % 2 === 0;
              return {
                style: {
                  backgroundColor: isEven ? theme.colors.background : theme.colors.backgroundGray,
                  transition: 'background-color 0.2s ease',
                },
                onMouseEnter: (e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.infoLight;
                },
                onMouseLeave: (e) => {
                  e.currentTarget.style.backgroundColor = isEven ? theme.colors.background : theme.colors.backgroundGray;
                },
              };
            }}
          />
        </StandardCard>
      )}
    </PageWrapper>
  );
};

export default EmisionContratos;
